/**
 * Planar Gizmo Service
 *
 * Enables two plane drag handles on the Inspector's position gizmo:
 * 1. XZ plane (ground) — always visible
 * 2. XY or YZ plane (vertical) — auto-switches to whichever is most face-on
 *    to the camera, so the user always has a usable vertical plane handle.
 *
 * All plane handles are semi-transparent and positioned in one quadrant.
 *
 * Uses GizmoManager.prototype.positionGizmoEnabled interception to discover
 * PositionGizmo instances created by the Inspector.
 */

import type { ServiceDefinition, IGizmoService } from '@babylonjs/inspector';
import { GizmoServiceIdentity } from '@babylonjs/inspector';
import { GizmoManager } from '@babylonjs/core/Gizmos/gizmoManager';
import type { PositionGizmo } from '@babylonjs/core/Gizmos/positionGizmo';
import type { IPlaneDragGizmo } from '@babylonjs/core/Gizmos/planeDragGizmo';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

// Track customized instances
const customizedInstances = new WeakSet<PositionGizmo>();

/**
 * Customize a plane gizmo to be semi-transparent and positioned in one quadrant.
 */
/**
 * Customize a plane gizmo's appearance.
 * @param planeNormal Which axis is the plane's normal: 'x' | 'y' | 'z'.
 *   - 'y' → XZ plane, offset in X and Z
 *   - 'x' → YZ plane, offset in Y and Z
 *   - 'z' → XY plane, offset in X and Y
 */
function customizePlaneGizmo(planeGizmo: IPlaneDragGizmo, planeNormal: 'x' | 'y' | 'z'): void {
    if (planeGizmo.coloredMaterial) {
        planeGizmo.coloredMaterial.alpha = 0.4;
    }
    if (planeGizmo.hoverMaterial) {
        planeGizmo.hoverMaterial.alpha = 0.6;
    }

    const gizmoMesh = (planeGizmo as unknown as { _gizmoMesh?: TransformNode })._gizmoMesh;
    if (gizmoMesh) {
        const halfSize = 0.1375 * (1 / 3) * 0.7 * 0.5;
        // Offset along the two axes that lie in the plane (not the normal axis)
        if (planeNormal === 'y') {
            gizmoMesh.position.x = halfSize;
            gizmoMesh.position.z = halfSize;
        } else if (planeNormal === 'x') {
            gizmoMesh.position.y = halfSize;
            gizmoMesh.position.z = halfSize;
        } else {
            gizmoMesh.position.x = halfSize;
            gizmoMesh.position.y = halfSize;
        }
        gizmoMesh.scaling.scaleInPlace(0.7);
    }
}

/**
 * Enable and customize plane gizmos on a PositionGizmo instance.
 */
function enablePlaneGizmos(positionGizmo: PositionGizmo): void {
    if (customizedInstances.has(positionGizmo)) {
        return;
    }
    customizedInstances.add(positionGizmo);

    // Enable all three plane gizmos
    if (positionGizmo.yPlaneGizmo) {
        positionGizmo.yPlaneGizmo.isEnabled = true;
        customizePlaneGizmo(positionGizmo.yPlaneGizmo, 'y');
    }
    if (positionGizmo.xPlaneGizmo) {
        positionGizmo.xPlaneGizmo.isEnabled = true;
        customizePlaneGizmo(positionGizmo.xPlaneGizmo, 'x');
    }
    if (positionGizmo.zPlaneGizmo) {
        positionGizmo.zPlaneGizmo.isEnabled = true;
        customizePlaneGizmo(positionGizmo.zPlaneGizmo, 'z');
    }

    console.log('[Planar Gizmo] Enabled all 3 plane gizmos');
}

// ============================================================================
// GizmoManager interception
// ============================================================================

let originalDesc: PropertyDescriptor | undefined;

function patchGizmoManager(): void {
    originalDesc = Object.getOwnPropertyDescriptor(
        GizmoManager.prototype,
        'positionGizmoEnabled'
    );

    if (!originalDesc?.set) return;

    // eslint-disable-next-line @typescript-eslint/unbound-method -- property descriptor accessors are re-bound via .call(this, ...)
    const origSet = originalDesc.set;
    // eslint-disable-next-line @typescript-eslint/unbound-method -- property descriptor accessors are re-bound via .call(this, ...)
    const origGet = originalDesc.get;

    Object.defineProperty(GizmoManager.prototype, 'positionGizmoEnabled', {
        get: origGet,
        set: function(this: GizmoManager, value: boolean) {
            origSet.call(this, value);
            if (value) {
                const gizmos = (this as unknown as {
                    gizmos: { positionGizmo?: PositionGizmo }
                }).gizmos;
                if (gizmos.positionGizmo) {
                    enablePlaneGizmos(gizmos.positionGizmo);
                }
            }
        },
        configurable: true,
        enumerable: true,
    });
}

function unpatchGizmoManager(): void {
    if (originalDesc) {
        Object.defineProperty(
            GizmoManager.prototype,
            'positionGizmoEnabled',
            originalDesc
        );
    }
}

/**
 * Service definition for enabling plane gizmos in Inspector V2.
 */
export const PlanarGizmoServiceDefinition: ServiceDefinition<[], [IGizmoService]> = {
    friendlyName: 'Planar Gizmo Service',

    consumes: [GizmoServiceIdentity],

    factory: (gizmoService: IGizmoService) => {
        patchGizmoManager();

        // If translate mode is already active, re-trigger to customize
        if (gizmoService.gizmoMode === 'translate') {
            gizmoService.gizmoMode = undefined;
            gizmoService.gizmoMode = 'translate';
        }

        console.log('[Planar Gizmo] Enabled XZ + adaptive vertical plane gizmos');

        return {
            dispose: () => {
                console.log('[Planar Gizmo] Disposing planar gizmo service');
                unpatchGizmoManager();
            }
        };
    }
};
